import React from 'react';
import { DollarSign } from 'lucide-react';

const MODELS = [
  { value: 'flat_per_lead', label: 'Per Lead Cost' },
  { value: 'recurring_budget', label: 'Weekly / Monthly Ad Spend' },
  { value: 'none', label: 'Zero / Company Provided' },
];

/**
 * Vendor cost configuration (drives automatic per-lead cost stamping + ROI).
 * @param {Object} props
 * @param {{pricingModel: string, costPerLead: string|number, recurringBudget: {amount: string|number, interval: string}}} props.form
 * @param {(path: string, value: *) => void} props.setField
 * @returns {JSX.Element}
 */
export default function CostSettingsSection({ form, setField }) {
  const model = form.pricingModel || 'flat_per_lead';
  const budget = form.recurringBudget || { amount: '', interval: 'weekly' };
  return (
    <div className="panel p-6" data-testid="cost-settings-section">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-4 h-4 text-civix-purpleSoft" />
        <h2 className="font-semibold">Cost settings</h2>
      </div>
      <div className="space-y-3">
        {MODELS.map((m) => (
          <label key={m.value} className="flex flex-wrap items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="pricing-model"
              value={m.value}
              checked={model === m.value}
              onChange={() => setField('pricingModel', m.value)}
              className="accent-civix-purple"
            />
            <span className="text-sm text-white/80 min-w-[200px]">{m.label}</span>
            {m.value === 'flat_per_lead' && model === m.value && (
              <span className="flex items-center gap-2">
                <span className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">$</span>
                  <input
                    className="input w-28 pl-7"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.costPerLead}
                    onChange={(e) => setField('costPerLead', e.target.value)}
                    placeholder="25.00"
                    aria-label="Cost per lead"
                  />
                </span>
                <span className="text-xs text-white/50">per lead</span>
              </span>
            )}
            {m.value === 'recurring_budget' && model === m.value && (
              <span className="flex items-center gap-2">
                <span className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">$</span>
                  <input
                    className="input w-32 pl-7"
                    type="number"
                    min="0"
                    step="0.01"
                    value={budget.amount}
                    onChange={(e) => setField('recurringBudget', { ...budget, amount: e.target.value })}
                    placeholder="1500.00"
                    aria-label="Ad spend amount"
                  />
                </span>
                <select
                  className="input"
                  value={budget.interval}
                  onChange={(e) => setField('recurringBudget', { ...budget, interval: e.target.value })}
                  aria-label="Ad spend interval"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </span>
            )}
          </label>
        ))}
      </div>
      <p className="text-xs text-white/45 mt-3">
        Costs are stamped on each lead automatically at intake. Ad spend is amortized across the reporting period.
      </p>
    </div>
  );
}
